import { motion } from "framer-motion";
import { useAutofill } from "../context/AutofillContext";
import { Button } from "./ui/Button";

/**
 * Tells the agent what we filled in on this step and where it came from.
 * Autofill that happens invisibly is indistinguishable from a bug — this is the
 * disclosure that makes it trustworthy, so it is scoped to the step in front of
 * them rather than summarising the whole form once and disappearing.
 */
export function AutofillBanner({ fieldIds }: { fieldIds: string[] }) {
  const autofill = useAutofill();
  if (!autofill?.plan || autofill.dismissed) return null;

  const filled = fieldIds.filter((fieldId) => autofill.isAutofilled(fieldId));
  const needingReview = filled.filter((fieldId) =>
    autofill.reviewFieldIds.includes(fieldId)
  );
  const offered = fieldIds.filter((fieldId) =>
    autofill.plan?.sensitiveFieldIds.includes(fieldId)
  );
  if (filled.length === 0 && offered.length === 0) return null;

  const newest = autofill.plan.sources[0];

  return (
    <motion.aside
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      aria-label="Autofill summary"
      className="col-span-12 rounded-card border-2 border-accent bg-accent-muted p-5"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-2">
          <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-accent">
            Filled from your record
          </p>
          {filled.length > 0 && (
            <p className="text-sm leading-relaxed text-content-primary">
              We pre-filled <strong>{filled.length}</strong>{" "}
              {filled.length === 1 ? "answer" : "answers"} on this step
              {newest ? (
                <>
                  {" "}
                  from your <strong>{newest.carrierLabel}</strong> contract and your profile
                </>
              ) : (
                " from your profile"
              )}
              . Every one is editable.
            </p>
          )}
          {needingReview.length > 0 && (
            <p className="text-sm text-content-secondary">
              {needingReview.length}{" "}
              {needingReview.length === 1 ? "answer is" : "answers are"} marked{" "}
              <span className="font-semibold">Confirm</span> — they changed between contracts or
              are old enough to be worth a second look.
            </p>
          )}
          {offered.length > 0 && (
            <p className="text-sm text-content-secondary">
              Sensitive details are never filled in for you. Use{" "}
              <span className="font-semibold">Use saved value</span> to copy one forward.
            </p>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={autofill.dismiss}>
          Got it
        </Button>
      </div>
    </motion.aside>
  );
}
